/**
 * IssueBreakdown - Shows SEO issues grouped by severity with expandable page URLs
 */

import { useState, useEffect } from 'react'
import { AlertCircle, AlertTriangle, Info, CheckCircle2, ChevronRight, ExternalLink, Loader2 } from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import type { ParsedIssues, SeoIssue, AuditPage } from './types'

interface IssueBreakdownProps {
  issues: ParsedIssues
  domain?: string // Domain for fetching page-level data
}

export function IssueBreakdown({ issues, domain }: IssueBreakdownProps) {
  const hasAnyIssues =
    issues.byStatus.error.length > 0 ||
    issues.byStatus.warning.length > 0 ||
    issues.byStatus.notice.length > 0

  if (!hasAnyIssues) {
    return (
      <div className="flex items-center gap-2 p-4 bg-green-500/10 rounded-lg">
        <CheckCircle2 className="h-5 w-5 text-green-500" />
        <span className="text-sm font-medium text-green-600 dark:text-green-400">
          All checks passed - no issues found
        </span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {issues.byStatus.error.length > 0 && (
        <IssueGroup
          title="Critical Issues"
          issues={issues.byStatus.error}
          icon={<AlertCircle className="h-4 w-4" />}
          variant="destructive"
          domain={domain}
        />
      )}

      {issues.byStatus.warning.length > 0 && (
        <IssueGroup
          title="Warnings"
          issues={issues.byStatus.warning}
          icon={<AlertTriangle className="h-4 w-4" />}
          variant="warning"
          domain={domain}
        />
      )}

      {issues.byStatus.notice.length > 0 && (
        <IssueGroup
          title="Notices"
          issues={issues.byStatus.notice}
          icon={<Info className="h-4 w-4" />}
          variant="notice"
          domain={domain}
        />
      )}
    </div>
  )
}

interface IssueGroupProps {
  title: string
  issues: SeoIssue[]
  icon: React.ReactNode
  variant: 'destructive' | 'warning' | 'notice'
  domain?: string
}

function IssueGroup({ title, issues, icon, variant, domain }: IssueGroupProps) {
  const bgClass =
    variant === 'destructive'
      ? 'bg-red-500/10 border-red-500/20'
      : variant === 'warning'
        ? 'bg-yellow-500/10 border-yellow-500/20'
        : 'bg-blue-500/10 border-blue-500/20'

  const iconClass =
    variant === 'destructive'
      ? 'text-red-500'
      : variant === 'warning'
        ? 'text-yellow-500'
        : 'text-blue-500'

  const titleClass =
    variant === 'destructive'
      ? 'text-red-600 dark:text-red-400'
      : variant === 'warning'
        ? 'text-yellow-600 dark:text-yellow-400'
        : 'text-blue-600 dark:text-blue-400'

  return (
    <div className={`rounded-lg border p-3 ${bgClass}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={iconClass}>{icon}</span>
        <span className={`font-medium text-sm ${titleClass}`}>
          {title} ({issues.length})
        </span>
      </div>
      <ul className="space-y-1">
        {issues.map((issue) => (
          <IssueItem
            key={`${issue.sectionUid}-${issue.code}`}
            issue={issue}
            domain={domain}
          />
        ))}
      </ul>
    </div>
  )
}

interface IssueItemProps {
  issue: SeoIssue
  domain?: string
}

function IssueItem({ issue, domain }: IssueItemProps) {
  const [expanded, setExpanded] = useState(false)
  const [pages, setPages] = useState<AuditPage[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  // Fetch pages when expanded for the first time
  useEffect(() => {
    if (expanded && !loaded && domain && issue.value > 0) {
      setLoading(true)
      fetch(`/api/seo/audit/${encodeURIComponent(domain)}/pages?issue=${encodeURIComponent(issue.code)}`)
        .then((res) => res.json() as Promise<{ pages: AuditPage[] }>)
        .then((data) => {
          setPages(data.pages || [])
          setLoaded(true)
        })
        .catch((err) => {
          console.error('Failed to fetch pages:', err)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [expanded, loaded, domain, issue.code, issue.value])

  // If no pages affected or no domain provided, show simple view
  if (issue.value === 0 || !domain) {
    return (
      <li className="text-sm py-1 ml-6">
        <span className="text-foreground">{issue.name}</span>
        {issue.value > 0 && (
          <span className="text-muted-foreground ml-1">
            ({issue.value} {issue.value === 1 ? 'page' : 'pages'})
          </span>
        )}
      </li>
    )
  }

  return (
    <li className="text-sm">
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger className="flex items-center gap-1 py-1 hover:bg-black/5 dark:hover:bg-white/5 rounded px-1 -mx-1 w-full text-left transition-colors">
          <ChevronRight
            className={`h-3 w-3 text-muted-foreground shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
          <span className="text-foreground">{issue.name}</span>
          <span className="text-muted-foreground ml-1">
            ({issue.value} {issue.value === 1 ? 'page' : 'pages'})
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="ml-4 mt-1 mb-2 pl-3 border-l-2 border-muted">
            {loading && (
              <div className="flex items-center gap-2 py-2 text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span className="text-xs">Loading affected pages...</span>
              </div>
            )}
            {!loading && pages.length === 0 && loaded && (
              <p className="text-xs text-muted-foreground py-1">
                No page data available
              </p>
            )}
            {!loading && pages.length > 0 && (
              <ul className="space-y-1 py-1">
                {pages.slice(0, 10).map((page) => (
                  <li key={page.id} className="flex items-center gap-1 group">
                    <a
                      href={page.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-muted-foreground hover:text-foreground truncate max-w-[300px]"
                      title={page.url}
                    >
                      {getRelativePath(page.url)}
                    </a>
                    <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </li>
                ))}
                {pages.length > 10 && (
                  <li className="text-xs text-muted-foreground pt-1">
                    ...and {pages.length - 10} more pages
                  </li>
                )}
              </ul>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </li>
  )
}

/**
 * Extract relative path from full URL for cleaner display
 */
function getRelativePath(url: string): string {
  try {
    const parsed = new URL(url)
    return parsed.pathname + parsed.search || '/'
  } catch {
    return url
  }
}
